import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Popover from "@radix-ui/react-popover";
import {
  Bell,
  Boxes,
  ChartLine,
  FileVideo,
  LayoutDashboard,
  Link2,
  ListVideo,
  LogOut,
  Megaphone,
  Send,
  Settings,
  Users,
  Webhook,
} from "lucide-react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

function NavItem({ to, label, icon: Icon }: { to: string; label: string; icon: typeof Bell }) {
  return (
    <NavLink
      to={to}
      end={to === "/admin"}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium",
          "text-foreground/80 hover:text-foreground hover:bg-foreground/5",
          isActive && "bg-gold/15 text-gold",
        )
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  );
}

type Notification = {
  id: string;
  type: string;
  title: string;
  body: string | null;
  href: string | null;
  isRead: boolean;
  createdAt: string;
};

function NotificationBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<{ items: Notification[] }>("/api/admin/notifications"),
    refetchInterval: 30_000,
  });
  const items = data?.items ?? [];
  const unread = items.filter((i) => !i.isRead).length;
  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/notifications/${id}/read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const markAllRead = useMutation({
    mutationFn: () => api.post("/api/admin/notifications/read-all", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className="relative inline-flex p-2 rounded-md hover:bg-foreground/5">
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-gold text-navy rounded-full text-[10px] leading-none px-1.5 py-0.5 font-semibold">
            {unread}
          </span>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="w-96 max-h-[70vh] overflow-hidden bg-admin-panel border border-admin-border rounded-md shadow-lg z-50 flex flex-col"
        >
          <div className="flex items-center justify-between p-3 rule-gold">
            <div className="font-head font-semibold text-sm">Notifications</div>
            {unread > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="text-xs text-electric hover:underline"
                disabled={markAllRead.isPending}
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {items.length === 0 && <p className="text-xs text-muted-foreground text-center py-8">No notifications</p>}
            <ul>
              {items.map((n) => (
                <li key={n.id}>
                  <Link
                    to={n.href ?? "/admin"}
                    onClick={() => { setOpen(false); if (!n.isRead) markRead.mutate(n.id); }}
                    className={cn(
                      "block px-3 py-2 hover:bg-foreground/5 border-b border-admin-rule",
                      !n.isRead && "bg-gold/5",
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {!n.isRead && <span className="mt-1.5 h-2 w-2 rounded-full bg-gold shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium line-clamp-1">{n.title}</div>
                        {n.body && <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>}
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {new Date(n.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function Inner() {
  const { user, loading, logout, flags } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  // S14 cutover (reversible flag): when strict, the campaign workspace is the
  // only way in — hide the global cross-campaign pages from the nav (their
  // routes also redirect; see App.tsx). Requires the base workspace flow too,
  // so flipping strict alone can't strand the user with nowhere to go.
  const strict = flags.workspaceStrict && flags.campaignWorkspaces;
  useEffect(() => {
    if (!loading && !user) nav("/login?from=" + encodeURIComponent(loc.pathname));
  }, [user, loading, nav, loc.pathname]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-admin-surface">
        <Logo variant="wordmark" className="text-2xl opacity-50" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-admin-surface text-foreground flex">
      {/* Bug 15 (phase 2): pin the sidebar to the viewport (h-screen sticky)
          so the user box + logout stay visible on tall pages — previously the
          sidebar stretched with the page and pushed logout below the fold. */}
      <aside className="w-60 shrink-0 bg-admin-panel border-r border-admin-border flex flex-col h-screen sticky top-0">
        <div className="p-4 rule-gold">
          <Link to="/admin" className="flex items-center gap-2">
            <Logo variant="wordmark" className="text-2xl" />
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {!strict && <NavItem to="/admin" label="Dashboard" icon={LayoutDashboard} />}
          {!strict && <NavItem to="/admin/library" label="Library" icon={FileVideo} />}
          <NavItem to="/admin/campaigns" label="Campaigns" icon={Megaphone} />
          {!strict && <NavItem to="/admin/playlists" label="Playlists" icon={ListVideo} />}
          {!strict && <NavItem to="/admin/internal" label="Internal Playlists" icon={Boxes} />}
          {!strict && <NavItem to="/admin/issuances" label="Demo Packs" icon={Send} />}
          {!strict && <NavItem to="/admin/demo-packs" label="Shared Links" icon={Link2} />}
          {!strict && <NavItem to="/admin/analytics" label="Analytics" icon={ChartLine} />}
          <div className="border-t border-admin-rule my-3" />
          {(user.role === "super_admin" || user.role === "people_ops") && (
            <NavItem to="/admin/users" label="Users" icon={Users} />
          )}
          {user.role === "super_admin" && (
            <>
              <NavItem to="/admin/events" label="Events" icon={Webhook} />
              <NavItem to="/admin/settings" label="Settings" icon={Settings} />
            </>
          )}
        </nav>
        <div className="p-3 border-t border-admin-rule">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs">
              <div className="font-medium truncate">{user.name}</div>
              <div className="text-muted-foreground text-[11px]">{user.role}</div>
            </div>
            <button
              onClick={() => logout().then(() => nav("/login"))}
              className="p-2 rounded-md hover:bg-foreground/5"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-admin-rule px-6 flex items-center justify-between">
          <h2 className="font-head text-base font-semibold capitalize">
            {loc.pathname.split("/")[2] || "Dashboard"}
          </h2>
          <div className="flex items-center gap-2">
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function AdminLayout() {
  return (
    <AuthProvider>
      <Inner />
    </AuthProvider>
  );
}
